import React from 'react'
import { motion } from 'framer-motion'
import { useAnimations } from '../contexts/AnimationContext'

interface FigmaUIElementsProps {
  children: React.ReactNode
  className?: string
}

// Figma-exact animation variants
const containerVariants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: { 
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1], // Figma's ease-out curve
      staggerChildren: 0.1
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    }
  }
}

const itemVariants = {
  hidden: { 
    opacity: 0,
    y: 30,
    scale: 0.9,
    filter: 'blur(4px)'
  },
  visible: { 
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  }
}

const floatingVariants = {
  idle: {
    y: [0, -10, 0],
    rotate: [0, 1, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
}

const glowVariants = {
  idle: {
    filter: [
      'drop-shadow(0 0 20px rgba(168, 85, 247, 0.3))',
      'drop-shadow(0 0 40px rgba(168, 85, 247, 0.5))',
      'drop-shadow(0 0 20px rgba(168, 85, 247, 0.3))'
    ],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
}

const FigmaUIElements: React.FC<FigmaUIElementsProps> = ({ children, className = '' }) => {
  const { settings } = useAnimations()

  // Button component with Figma animations
  const FigmaButton = ({ onClick, children, variant = 'primary', className = '', ...props }: any) => (
    <motion.button
      onClick={onClick}
      className={`
        relative px-6 py-3 rounded-lg font-medium transition-all duration-200
        ${variant === 'primary' 
          ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/25' 
          : 'bg-gray-800 text-gray-200 border border-gray-700'
        }
        ${className}
      `}
      variants={settings.transitions ? itemVariants : {}}
      whileHover={settings.enabled ? {
        scale: 1.02,
        y: -2,
        boxShadow: variant === 'primary' 
          ? '0 10px 40px rgba(168, 85, 247, 0.4)' 
          : '0 10px 30px rgba(0, 0, 0, 0.3)',
        transition: { duration: 0.2 }
      } : {}}
      whileTap={settings.enabled ? {
        scale: 0.98,
        y: 0,
        transition: { duration: 0.1 }
      } : {}}
      animate={settings.floatingElements && variant === 'primary' ? floatingVariants.idle : {}}
      {...props}
    >
      {settings.glowEffects && variant === 'primary' && (
        <motion.div
          className="absolute inset-0 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600 opacity-50 blur-md"
          animate={glowVariants.idle}
          style={{ zIndex: -1 }}
        />
      )}
      {children}
    </motion.button>
  )

  // Card component with Figma animations
  const FigmaCard = ({ children, className = '', hover = true, ...props }: any) => (
    <motion.div
      className={`
        bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6
        ${className}
      `}
      variants={settings.transitions ? itemVariants : {}}
      whileHover={settings.enabled && hover ? {
        scale: 1.02,
        y: -4,
        borderColor: 'rgba(168, 85, 247, 0.5)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(168, 85, 247, 0.2)',
        transition: { duration: 0.3 }
      } : {}}
      animate={settings.floatingElements ? {
        y: [0, -5, 0],
        transition: {
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: Math.random() * 2
        }
      } : {}}
      {...props}
    >
      {children}
    </motion.div>
  )

  // Input component with Figma animations
  const FigmaInput = ({ className = '', label, ...props }: any) => (
    <motion.div
      className="relative"
      variants={settings.transitions ? itemVariants : {}}
    >
      {label && (
        <motion.label
          className="block text-sm font-medium text-gray-300 mb-2"
          animate={settings.enabled ? {
            opacity: [0.7, 1, 0.7],
            transition: { duration: 2, repeat: Infinity }
          } : {}}
        >
          {label}
        </motion.label>
      )}
      <motion.input
        className={`
          w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg
          text-white placeholder-gray-400 transition-all duration-200
          focus:border-purple-500 focus:bg-gray-800/70 focus:outline-none
          ${className}
        `}
        whileFocus={settings.enabled ? {
          scale: 1.01,
          boxShadow: '0 0 0 3px rgba(168, 85, 247, 0.1)',
          transition: { duration: 0.2 }
        } : {}}
        {...props}
      />
    </motion.div>
  )

  // Icon component with Figma animations
  const FigmaIcon = ({ children, className = '', spin = false, pulse = false, ...props }: any) => (
    <motion.div
      className={`inline-flex items-center justify-center ${className}`}
      animate={settings.enabled ? {
        ...(spin && {
          rotate: [0, 360],
          transition: { duration: 2, repeat: Infinity, ease: 'linear' }
        }),
        ...(pulse && {
          scale: [1, 1.1, 1],
          opacity: [0.8, 1, 0.8],
          transition: { duration: 1.5, repeat: Infinity }
        })
      } : {}}
      whileHover={settings.enabled ? {
        scale: 1.1,
        rotate: spin ? undefined : 5,
        transition: { duration: 0.2 }
      } : {}}
      {...props}
    >
      {children}
    </motion.div>
  )

  // Text animation component
  const FigmaText = ({ children, className = '', typewriter = false, gradient = false, ...props }: any) => {
    const text = children?.toString() || ''
    
    return (
      <motion.div
        className={`
          ${gradient ? 'bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent' : ''}
          ${className}
        `}
        variants={settings.transitions ? itemVariants : {}}
        {...props}
      >
        {typewriter && settings.enabled ? (
          <motion.span
            initial={{ width: 0 }}
            animate={{ width: 'auto' }}
            transition={{ duration: text.length * 0.05, ease: 'easeOut' }}
            style={{ overflow: 'hidden', whiteSpace: 'nowrap', display: 'inline-block' }}
          >
            {text}
          </motion.span>
        ) : (
          children
        )}
      </motion.div>
    )
  }

  // Main container
  return (
    <motion.div
      className={className}
      variants={settings.transitions ? containerVariants : {}}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      {React.cloneElement(children as React.ReactElement, {
        FigmaButton,
        FigmaCard,
        FigmaInput,
        FigmaIcon,
        FigmaText
      })}
    </motion.div>
  )
}

// Export individual components for direct usage
export const FigmaButton = ({ onClick, children, variant = 'primary', className = '', ...props }: any) => {
  const { settings } = useAnimations()
  
  return (
    <motion.button
      onClick={onClick}
      className={`
        relative px-6 py-3 rounded-lg font-medium transition-all duration-200
        ${variant === 'primary' 
          ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-500/25' 
          : 'bg-gray-800 text-gray-200 border border-gray-700'
        }
        ${className}
      `}
      whileHover={settings.enabled ? {
        scale: 1.02,
        y: -2,
        boxShadow: variant === 'primary' 
          ? '0 10px 40px rgba(168, 85, 247, 0.4)' 
          : '0 10px 30px rgba(0, 0, 0, 0.3)',
        transition: { duration: 0.2 }
      } : {}}
      whileTap={settings.enabled ? {
        scale: 0.98,
        y: 0,
        transition: { duration: 0.1 }
      } : {}}
      animate={settings.floatingElements && variant === 'primary' ? floatingVariants.idle : {}}
      {...props}
    >
      {settings.glowEffects && variant === 'primary' && (
        <motion.div
          className="absolute inset-0 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600 opacity-50 blur-md"
          animate={glowVariants.idle}
          style={{ zIndex: -1 }}
        />
      )}
      {children}
    </motion.button>
  )
}

export const FigmaCard = ({ children, className = '', hover = true, ...props }: any) => {
  const { settings } = useAnimations()
  
  return (
    <motion.div
      className={`
        bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6
        ${className}
      `}
      whileHover={settings.enabled && hover ? {
        scale: 1.02,
        y: -4,
        borderColor: 'rgba(168, 85, 247, 0.5)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(168, 85, 247, 0.2)',
        transition: { duration: 0.3 }
      } : {}}
      animate={settings.floatingElements ? {
        y: [0, -5, 0],
        transition: {
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: Math.random() * 2
        }
      } : {}}
      {...props}
    >
      {children}
    </motion.div>
  )
}

export default FigmaUIElements